import type { ReactNode } from 'react';
import type { CohortBoardPayload } from '@/components/CohortBoard';

type LedgerFinding = {
  term?: string;
  disposition?: string;
  fetch?: string;
  fact?: string;
  hole?: string;
  constraint_class?: string;
};

type LedgerNeed = {
  after_term?: string;
  still_need?: string;
  acquire_via?: string;
  status?: string;
  landing_url?: string;
  hole?: string;
};

type LedgerHop = {
  id?: string;
  kind?: string;
  outcome?: string;
  question?: string;
  unused_class?: string;
  finding?: string;
};

type LedgerExhaustCall = {
  id?: string;
  tool?: string;
  title?: string;
  status?: string;
  n?: number;
  note?: string;
};

type LedgerExhaust = {
  legal_name?: string;
  queries?: string[];
  stage?: string;
  hit?: number;
  empty?: number;
  blocked?: number;
  listed?: number;
  indexed_not_fetched?: number;
  calls?: LedgerExhaustCall[];
};

export function isResearchLedger(board?: CohortBoardPayload | null): boolean {
  return board?.layout === 'research_ledger';
}

function Badge({ label, tone }: { label: string; tone: string }) {
  const cls =
    tone === 'kept'
      ? 'bg-emerald-50 text-emerald-800'
      : tone === 'conceded' || tone === 'unprinted' || tone === 'blocked'
        ? 'bg-amber-50 text-amber-900'
        : tone === 'dropped' || tone === 'empty'
          ? 'bg-gray-100 text-gray-500'
          : tone === 'fetched' || tone === 'closed' || tone === 'composed' || tone === 'killed' || tone === 'hit' || tone === 'listed'
            ? 'bg-sky-50 text-sky-800'
            : 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${cls}`}>
      {label}
    </span>
  );
}

export function ResearchLedger({
  board,
  status,
  memoSlot,
}: {
  board: CohortBoardPayload;
  status?: string;
  memoSlot?: ReactNode;
}) {
  const findings = (board.findings ?? []) as LedgerFinding[];
  const needs = (board.needs ?? []) as LedgerNeed[];
  const hops = (board.hops ?? []) as LedgerHop[];
  const exhaust = (board.exhaust ?? null) as LedgerExhaust | null;
  const harvestCalls = exhaust?.calls ?? [];
  const sources = board.sources ?? [];
  const running = status === 'queued' || status === 'running' || !board.finished;

  return (
    <div className="space-y-4" data-research-ledger>
      <div className="rounded-2xl border border-gray-200 bg-gray-50/80 px-4 py-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-sm font-medium text-gray-900">
            {running ? 'Warehouse ledger' : 'How this was built'}
          </p>
          <p className="text-xs text-gray-500">{board.headline}</p>
        </div>
        {board.question ? (
          <p className="mt-1 text-sm text-gray-700">{board.question}</p>
        ) : null}
        {board.frame?.identity ? (
          <p className="mt-2 text-sm leading-6 text-gray-800">{board.frame.identity}</p>
        ) : null}
      </div>

      {harvestCalls.length ? (
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Issuer harvest
          </h3>
          <p className="mb-2 text-xs text-gray-500">
            {exhaust?.legal_name ? `${exhaust.legal_name} · ` : ''}
            {exhaust?.hit ?? 0} hit · {exhaust?.empty ?? 0} empty · {exhaust?.blocked ?? 0}{' '}
            blocked
            {exhaust?.indexed_not_fetched
              ? ` · ${exhaust.indexed_not_fetched} filings indexed, text not pulled`
              : ''}
          </p>
          <ul className="space-y-1">
            {harvestCalls.map((row) => (
              <li key={row.id || row.title} className="text-sm leading-6 text-gray-800">
                <Badge label={String(row.status || 'call')} tone={String(row.status || '')} />{' '}
                <span className="font-medium">{row.title}</span>
                {row.n ? <span className="text-gray-500"> · {row.n}</span> : null}
                {row.note ? <span className="text-gray-500"> — {row.note}</span> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {findings.length ? (
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Findings
          </h3>
          <ul className="space-y-2">
            {findings.map((finding) => (
              <li
                key={finding.term}
                className="rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-800"
              >
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <Badge
                    label={finding.disposition || 'finding'}
                    tone={finding.disposition || ''}
                  />
                  <span className="font-medium">{finding.term}</span>
                </div>
                {finding.fact ? <p className="leading-6">{finding.fact}</p> : null}
                {finding.fetch ? (
                  <p className="mt-1 text-xs text-gray-500">File: {finding.fetch}</p>
                ) : null}
                {finding.hole ? (
                  <p className="mt-1 text-xs text-amber-800">Hole: {finding.hole}</p>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : (
        <p className="text-sm text-gray-500">
          {harvestCalls.length
            ? 'Harvest is on disk. Discriminating next — findings will land here.'
            : 'Framing, then fetching. Findings will land here.'}
        </p>
      )}

      {needs.length ? (
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            What else / how we got it
          </h3>
          <ul className="space-y-2">
            {needs.map((need, i) => (
              <li key={`${need.after_term}-${i}`} className="text-sm leading-6 text-gray-800">
                <Badge label={need.status || need.acquire_via || 'need'} tone={need.status || ''} />{' '}
                After <span className="font-medium">{need.after_term}</span> → {need.still_need}
                {need.landing_url ? (
                  <>
                    {' '}
                    <a
                      href={need.landing_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 underline decoration-blue-200 underline-offset-2"
                    >
                      {need.acquire_via}
                    </a>
                  </>
                ) : need.acquire_via ? (
                  <span className="text-gray-500"> ({need.acquire_via})</span>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {hops.length ? (
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Hops
          </h3>
          <ul className="space-y-2">
            {hops.map((hop) => (
              <li key={hop.id || hop.question} className="text-sm leading-6 text-gray-800">
                <Badge label={hop.outcome || 'open'} tone={hop.outcome || ''} />{' '}
                <span className="font-medium">{hop.kind}</span>
                {hop.unused_class ? (
                  <span className="text-gray-500"> · {hop.unused_class}</span>
                ) : null}
                <div className="text-gray-700">{hop.finding || hop.question}</div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {sources.length ? (
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Files on disk
          </h3>
          <p className="text-xs leading-6 text-gray-600">{sources.join(' · ')}</p>
        </section>
      ) : null}

      {memoSlot ? <section className="border-t border-gray-200 pt-4">{memoSlot}</section> : null}
    </div>
  );
}
